import { useI18n } from "../i18n";
import type { LocalTask } from "../types/api";

interface Props {
  open: boolean;
  tasks: LocalTask[];
  // The device is offline or none selected — tasks can't be listed.
  offline: boolean;
  selectedKey: string | null;
  onClose: () => void;
  onSelect: (task: LocalTask) => void;
  onNew: () => void;
}

// Mirrors mole's resumability rules: codex batch jobs can't be resumed; for
// everything else a task id is enough. Non-resumable tasks are still viewable.
export function isTaskResumable(task: LocalTask): boolean {
  if (task.provider === "claude") return Boolean(task.id);
  if (task.provider === "codex") return Boolean(task.id) && !task.kind.includes("job");
  return false;
}

function relativeTime(ms: number | null | undefined, lang: string): string {
  if (!ms) return "";
  const diff = ms - Date.now();
  const abs = Math.abs(diff);
  const min = 60_000;
  const hr = 3_600_000;
  const day = 86_400_000;
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
  if (abs < hr) return rtf.format(Math.round(diff / min), "minute");
  if (abs < day) return rtf.format(Math.round(diff / hr), "hour");
  if (abs < 7 * day) return rtf.format(Math.round(diff / day), "day");
  return new Date(ms).toLocaleDateString(lang, { month: "short", day: "numeric" });
}

// Map mole task statuses onto the history dot colors used for cloud sessions.
function dotStatus(status: string): string {
  if (status === "running") return "running";
  if (status === "failed" || status === "error") return "failed";
  return "completed";
}

/**
 * History drawer for a paired device: the device's recent local tasks, in the
 * same drawer layout the cloud agent uses for past conversations. Picking a
 * task opens it in the chat pane where it can be read and continued.
 */
export function LocalHistoryDrawer({ open, tasks, offline, selectedKey, onClose, onSelect, onNew }: Props) {
  const { t, lang } = useI18n();

  return (
    <div className={`drawer-root ${open ? "open" : ""}`} aria-hidden={!open}>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer-panel" role="dialog" aria-label={t("history.title")}>
        <div className="drawer-head">
          <span className="drawer-title">{t("local.tasksTitle")}</span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            ×
          </button>
        </div>
        <button
          type="button"
          className="history-new"
          onClick={() => {
            onNew();
            onClose();
          }}
        >
          ＋ {t("history.new")}
        </button>
        <div className="history-list">
          {offline ? (
            <p className="muted history-empty">{t("local.tasksOffline")}</p>
          ) : tasks.length === 0 ? (
            <p className="muted history-empty">{t("local.noTasks")}</p>
          ) : (
            tasks.map((task) => (
              <button
                key={task.detailKey}
                type="button"
                className={`history-item ${task.detailKey === selectedKey ? "active" : ""}`}
                onClick={() => {
                  onSelect(task);
                  onClose();
                }}
                title={task.title}
              >
                <span className="h-title">{task.title || "(untitled)"}</span>
                <span className="h-meta">
                  <span className={`h-dot ${dotStatus(task.status)}`} />
                  <span className="h-time">
                    {task.provider}
                    {task.updatedAt ? ` · ${relativeTime(task.updatedAt, lang)}` : ""}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}
